import { FC, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import ReactModal from 'react-modal';
import classNames from 'classnames/bind';
import { useSearchInputContext, type SearchRefs } from 'src/common/components/Search/SearchInputProvider';
import SheetDates from 'src/common/components/Search/SearchModalMobile/Sheet/SheetDates';
import SheetGuests from 'src/common/components/Search/SearchModalMobile/Sheet/SheetGuests';
import LocationAutocompleteList from 'src/common/components/Search/LocationAutocompleteList';
import InputLocation from 'src/common/components/Search/Inputs/InputLocation';
import InputDates from 'src/common/components/Search/Inputs/InputDates';
import InputGuests from 'src/common/components/Search/Inputs/InputGuests';
import SheetFooter from 'src/common/components/Search/SearchModalMobile/Sheet/SheetFooter';
import { useSearchHeroModalContext } from 'src/common/components/Search/SearchHero/SearchHeroModalProvider';
import { SearchComboboxProvider } from 'src/common/components/Search/SearchComboboxProvider';
import styles from './SearchModalMobile.module.scss';
import CloseButton from '../../CloseButton';

const cx = classNames.bind(styles);

const SearchModalMobile: FC = () => {
  const modal = useSearchHeroModalContext();
  const { sheet } = useSearchInputContext();

  const [t] = useTranslation('common');

  const refs: SearchRefs = {
    inputs: useRef<HTMLInputElement>(null),
    where: useRef<HTMLInputElement>(null),
    when: useRef<HTMLInputElement>(null),
    who: useRef<HTMLInputElement>(null),
    button: useRef<HTMLButtonElement>(null),
    capacityNumber: useRef<HTMLInputElement>(null),
  };

  const popoverRef = useRef<HTMLDivElement>(null);

  return (
    <ReactModal
      isOpen={sheet.active != null}
      onRequestClose={sheet.close}
      contentLabel="searchModal"
      overlayClassName={styles.overlay}
      className={styles.content}
    >
      <SearchComboboxProvider id="modal" refs={refs}>
        <div
          className={styles.root}
          data-cwv="search--modal"
        >
          <div className={styles.header}>
            <CloseButton
              onClick={sheet.close}
              aria-label={t('close-button-label')}
              size="large"
              data-cwv="modal--close"
              position="static"
            />
          </div>
          <div className={styles.scrollable}>
            <div className={cx(styles.inputs, modal.active)} ref={refs.inputs}>
              <InputLocation refs={refs} variant="sheet" popoverRef={popoverRef} />
              <InputDates closeIconId="x-circle" variant="sheet" refs={refs} />
              <InputGuests refs={refs} closeIconId="x-circle" variant="sheet" />
            </div>
            <div className={styles.pickers}>
              <LocationAutocompleteList shadow={false} refs={refs} variant="sheet" popoverRef={popoverRef} />
              {modal.isDatesOpen && (<SheetDates refs={refs} />)}
              {modal.isGuestsOpen && (<SheetGuests />)}
            </div>
          </div>
          <div className={styles.footer}>
            <SheetFooter />
          </div>
        </div>
      </SearchComboboxProvider>
    </ReactModal>
  );
};

export default SearchModalMobile;
